/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OrgField from './OrgField';

/**
 * A select that the page actually gets to style.
 *
 * A native `<select>` is the right default almost everywhere, and this one is
 * not that. The reason is narrow: the browser draws its option list at OS
 * level, outside the document, so none of the Dawn tokens reach it. Next to
 * these fields it arrived as an oversized list in the wrong typeface with a
 * system highlight colour belonging to no part of this design — the value
 * small and tidy, the list twice its weight.
 *
 * So the list is drawn in the page instead. That is a trade, and the cost is
 * real: a native select gives keyboard behaviour, type-ahead, mobile pickers
 * and accessibility for free, and all of it has to be rebuilt here. What is
 * rebuilt:
 *
 *   ↑ ↓        move through options, skipping group headings
 *   Enter/Space choose the focused option
 *   Escape      close and keep the current value
 *   Home/End    first and last option
 *   typing      jumps to the next option starting with what you typed, which
 *               is what makes a 400-entry timezone list usable at all
 *   Tab         closes, because focus is leaving
 *
 * It reports itself as a combobox owning a listbox, so a screen reader
 * announces it the same way it would announce the native one.
 */

/** How long consecutive keystrokes count as one type-ahead search. */
const TYPEAHEAD_MS = 700;

/** Flatten groups to a single ordered list; keeps group boundaries for render. */
function flatten(options, groups) {
  if (groups) {
    return groups.flatMap((group) => group.options.map((option) => ({ ...option, group: group.label })));
  }
  return options || [];
}

export default function OrgSelect({
  label, value, onChange, required, error, help,
  options, groups, placeholder = 'Choose…', loadError, onRetry,
  // Puts a filter box at the top of the open list.
  //
  // Type-ahead already jumps to the first match, which is enough for a list you
  // can hold in your head. It is not enough for 249 countries: it only matches
  // from the START of a name, gives no sight of how many matches there are, and
  // silently does nothing when you mistype. Turn this on for long lists.
  searchable = false,
  searchPlaceholder = 'Type to filter…',
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);
  const typeahead = useRef({ text: '', at: 0 });

  const allItems = useMemo(() => flatten(options, groups), [options, groups]);

  // Substring, not prefix: somebody looking for "United Arab Emirates" is as
  // likely to type "emirates", and a filter that only matched the first word
  // would leave them believing it is not in the list.
  const items = useMemo(() => {
    if (!searchable) return allItems;
    const needle = query.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((item) => String(item.label).toLowerCase().includes(needle));
  }, [allItems, searchable, query]);

  // Against the FULL list: the chosen value must still show in the button while
  // a filter that excludes it is open.
  const selected = useMemo(
    () => allItems.find((item) => String(item.value) === String(value)) || null,
    [allItems, value],
  );
  const selectedIndex = items.findIndex((item) => String(item.value) === String(value));

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    // Cleared on close so reopening shows the whole list. A filter that
    // persisted would look like a list that had lost most of its options.
    setQuery('');
    if (refocus) buttonRef.current?.focus();
  }, []);

  const commit = useCallback((item) => {
    onChange(String(item.value));
    close();
  }, [onChange, close]);

  // A click anywhere else means "leave it as it was" — the same as Escape, and
  // the behaviour a native select has.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (event) => {
      if (!rootRef.current?.contains(event.target)) close(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, close]);

  // Opening should land on what is already chosen, not at the top of a list of
  // four hundred timezones.
  useEffect(() => {
    if (open && searchable) {
      // Focused so the list can be filtered by typing straight away, which is
      // the whole point of turning it on.
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open, searchable]);

  useEffect(() => {
    if (open) setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    // Only when the list opens; moving through it afterwards is the keyboard's job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    // Optional call: scrollIntoView is absent in jsdom, and keeping a focused
    // option in view is a nicety. A missing browser API must not be able to
    // throw out of a form field.
    node?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  const move = (delta) => {
    if (!items.length) return;
    setActiveIndex((current) => {
      const from = current < 0 ? selectedIndex : current;
      const next = from + delta;
      if (next < 0) return 0;
      if (next > items.length - 1) return items.length - 1;
      return next;
    });
  };

  const jumpTo = (char) => {
    const now = Date.now();
    const state = typeahead.current;
    state.text = now - state.at > TYPEAHEAD_MS ? char : state.text + char;
    state.at = now;

    const query = state.text.toLowerCase();
    // Search after the current position first, so repeating a letter walks
    // through everything starting with it rather than sticking on the first.
    const from = (activeIndex < 0 ? selectedIndex : activeIndex) + (state.text.length > 1 ? 0 : 1);
    const ordered = [...items.slice(Math.max(from, 0)), ...items.slice(0, Math.max(from, 0))];
    const hit = ordered.find((item) => String(item.label).toLowerCase().startsWith(query));
    if (hit) setActiveIndex(items.indexOf(hit));
  };

  const onKeyDown = (event) => {
    if (event.key === 'Tab') { if (open) close(false); return; }

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); move(1); break;
      case 'ArrowUp': event.preventDefault(); move(-1); break;
      case 'Home': event.preventDefault(); setActiveIndex(0); break;
      case 'End': event.preventDefault(); setActiveIndex(items.length - 1); break;
      case 'Escape': event.preventDefault(); close(); break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (items[activeIndex]) commit(items[activeIndex]);
        break;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          jumpTo(event.key);
        }
    }
  };

  let lastGroup = null;

  return (
    <OrgField
      label={label}
      required={required}
      error={error}
      help={loadError ? undefined : help}
    >
      {({ id, invalid, describedBy }) => (
        <div className="orgf-combo" ref={rootRef}>
          <button
            id={id}
            ref={buttonRef}
            type="button"
            className={`orgf-combobtn${open ? ' open' : ''}${selected ? '' : ' placeholder'}`}
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={`${id}-list`}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            onClick={() => setOpen((current) => !current)}
            onKeyDown={onKeyDown}
          >
            <span className="orgf-combovalue">{selected ? selected.label : placeholder}</span>
            <span className="orgf-combocaret" aria-hidden="true" />
          </button>

          {open && (
            <ul
              id={`${id}-list`}
              ref={listRef}
              className="orgf-combolist"
              role="listbox"
              aria-label={label}
              tabIndex={-1}
            >
              {searchable && (
                <li className="orgf-combosearch" role="presentation">
                  <input
                    ref={searchRef}
                    type="text"
                    className="orgf-input"
                    value={query}
                    placeholder={searchPlaceholder}
                    aria-label={`Filter ${label}`}
                    // The list closes on outside mousedown; this is inside it,
                    // but stopping propagation keeps a click in the box from
                    // being read as a choice by the option handlers below.
                    onMouseDown={(event) => event.stopPropagation()}
                    onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }}
                    onKeyDown={(event) => {
                      // Enter takes the top match, which is what a filter
                      // implies. Everything else is left to the list's own
                      // handler so arrows and Escape keep working.
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        if (items[activeIndex]) commit(items[activeIndex]);
                      }
                    }}
                  />
                </li>
              )}
              {items.length === 0 && (
                <li className="orgf-comboempty" role="presentation">
                  {searchable && query.trim() ? `Nothing matches “${query.trim()}”` : 'Nothing to choose from'}
                </li>
              )}
              {items.map((item, index) => {
                const header = item.group && item.group !== lastGroup ? item.group : null;
                lastGroup = item.group ?? lastGroup;
                return (
                  <React.Fragment key={`${item.group || ''}-${item.value}`}>
                    {header && (
                      <li className="orgf-combogroup" role="presentation">{header}</li>
                    )}
                    <li
                      role="option"
                      data-index={index}
                      aria-selected={String(item.value) === String(value)}
                      className={`orgf-combopt${index === activeIndex ? ' active' : ''}`}
                      // mousedown rather than click: the document listener that
                      // closes on an outside click also fires on mousedown, and
                      // a click here would arrive after the list had gone.
                      onMouseDown={(event) => { event.preventDefault(); commit(item); }}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      {item.label}
                    </li>
                  </React.Fragment>
                );
              })}
            </ul>
          )}

          {loadError && (
            <span className="orgf-help">
              These choices could not be loaded.{' '}
              <button type="button" className="orgf-link" onClick={onRetry}>Retry</button>
            </span>
          )}
        </div>
      )}
    </OrgField>
  );
}
